import { ApiError } from "@/types/api";

// NEXT_PUBLIC_API_URL se define en .env.local como "/api" (relativa).
// Fallback a "/api" para que funcione en cualquier entorno sin config extra.
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "/api";

const TOKEN_KEY='token';

// localStorage persiste entre sesiones en móvil.
// sessionStorage se limpia al cerrar pestaña — causa pérdida de token en móvil.
function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage;
  } catch {
    return sessionStorage; // fallback si localStorage está bloqueado (modo privado con cuotas)
  }
}

function getToken(): string | null {
  return getStorage()?.getItem(TOKEN_KEY) ?? null;
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Queue of requests waiting for token refresh
type RefreshCallback = (token: string | null) => void;
let refreshSubscribers: RefreshCallback[] = [];

function subscribeToRefresh(cb: RefreshCallback) {
  refreshSubscribers.push(cb);
}

function notifyRefreshSubscribers(token: string | null) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

async function tryRefreshToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) return refreshPromise;

  const token = getToken();
  if (!token) return null;

  isRefreshing = true;
  refreshPromise = fetch(`${API_BASE}/auth/refresh/`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  })
    .then(async (res) => {
      // Si es 429 (rate limit), no redirigir — simplemente devolver null
      // El usuario seguirá autenticado con el token actual hasta que expire
      if (res.status === 429) return null;
      if (!res.ok) return null;
      const data = await res.json();
      if (data.token) {
        const storage = getStorage();
        if (storage) {
          storage.setItem(TOKEN_KEY, data.token);
          if (data.nombre) storage.setItem('auth_nombre', data.nombre);
        }
        return data.token as string;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
}

function clearSession(): void {
  const storage = getStorage();
  if (storage) {
    storage.removeItem(TOKEN_KEY);
    storage.removeItem('auth_nombre');
    storage.removeItem('auth_username');
  }
}

function redirectToLogin(reason: 'expired' | 'unauthorized' = 'unauthorized'): never {
  clearSession();
  if (typeof window !== "undefined") {
    window.location.href = reason === 'expired' ? "/login?session=expired" : "/login";
  }
  throw new ApiError(401, "No autorizado");
}

// Check if we are on the login page — if so, skip token refresh on 401
function isLoginPage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/login';
}

export interface ApiFetchOptions extends RequestInit {
  skipAuthRefresh?: boolean;
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
  _retry = true
): Promise<T> {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const method = (options.method ?? "GET").toUpperCase();
  if (["POST", "PUT", "PATCH"].includes(method) && options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch {
    throw new ApiError(0, "Sin conexión al servidor");
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;

    // Detectar contenido binario (PDF, imágenes, etc.)
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/pdf") || contentType.includes("application/octet-stream") || contentType.includes("application/zip")) {
      return response.blob() as Promise<T>;
    }

    return response.json() as Promise<T>;
  }

  // Error handling
  let errorMessage = response.statusText;
  try {
    const body = await response.json();
    if (Array.isArray(body?.detail)) {
      errorMessage = body.detail.map((e: unknown) => {
        if (typeof e === 'string') return e;
        const err = e as { loc?: string[]; msg?: string };
        return `${err.loc?.join('.') || 'error'}: ${err.msg || 'error'}`;
      }).join('; ');
    } else if (typeof body?.detail === 'string') {
      errorMessage = body.detail;
    } else {
      errorMessage = body?.message || body?.error || errorMessage;
    }
  } catch {
    // ignore parse errors
  }

  switch (response.status) {
    case 401:
      // If on login page, never try to refresh — just throw the error so the
      // login component can display it in the Alert. This avoids a redirect
      // loop when the user enters invalid credentials.
      if (isLoginPage() || options.skipAuthRefresh) {
        throw new ApiError(401, errorMessage);
      }
      if (_retry) {
        // If a refresh is already in progress, wait for it instead of starting another
        if (isRefreshing) {
          const newToken = await new Promise<string | null>((resolve) => {
            subscribeToRefresh(resolve);
          });
          if (newToken) {
            return apiFetch<T>(path, { ...options, skipAuthRefresh: true }, false);
          }
          return redirectToLogin('expired');
        }
        const newToken = await tryRefreshToken();
        notifyRefreshSubscribers(newToken);
        if (newToken) {
          return apiFetch<T>(path, { ...options, skipAuthRefresh: true }, false);
        }
        return redirectToLogin('expired');
      }
      return redirectToLogin();
    case 403:
      throw new ApiError(403, errorMessage);
    case 404:
      throw new ApiError(404, errorMessage);
    case 409:
      throw new ApiError(409, errorMessage);
    case 429:
      throw new ApiError(429, errorMessage || "Demasiados intentos. Espere unos minutos.");
    default:
      if (response.status >= 500) {
        throw new ApiError(response.status, errorMessage);
      }
      throw new ApiError(response.status, errorMessage);
  }
}
